import React from "react";
import * as attachmentService from "../../../../models/services/attachmentService";
import * as messageService from "../../../../models/services/messageService";
import { ISessionState } from "../../../../models/session/sessionState";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { AttachmentList } from "source/components/attachments/AttachmentList/AttachmentList";
import downloadjs from "downloadjs";
import { SaveDataObj } from "source/framework/saveData";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { EditItem } from "ui/Scripts/source/models/common/editItem";

interface IMessageEditDialogAttachmentsProps {
    sessionState: ISessionState;
    isLockedToOtherUser: boolean;
    isNew: boolean;
    companyId: string;
    messageId: string;
    attachments: IFileAttachment[];
    onChangeAttachments: (attachments: IFileAttachment[]) => void;
}

export const MessageEditDialogAttachments = (props: IMessageEditDialogAttachmentsProps) => {
    // #region Attachments
    const handleAttachmentDownload = async (id: string) => {
        if (!id) return;
        const attachment = props.attachments.find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    const getRemoveAttachmentsSaveData = (attachmentIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.messageId);
        result.append("companyId", props.companyId);
        result.append("ids", attachmentIds);
        return result;
    };

    const handleAttachmentRemove = async (id: string) => {
        if (!id) return;
        if (!props.isNew) {
            const saveData = getRemoveAttachmentsSaveData([id]);
            if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveAttachmentParameter, props.attachments.find(x => x.id === id)?.name), false) === ConfirmationDialogResult.No) return;
            const result = AppUtils.callService(() => messageService.messageEditRemoveAttachments(saveData.obj));
            if (!result) return;
        }
        props.onChangeAttachments(props.attachments.filter(i => i.id !== id));
    };

    const getAddAttachmentsSaveData = async (attachments: IFileAttachment[]): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("id", props.messageId);
        result.append("companyId", props.companyId);
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(attachments));
        return result;
    };
    
    const handleAttachmentsAdd = async (fileList: FileList) => {
        const attachments = await FileAttachment.addFileListToFileAttachments([], fileList, 0);
        if (attachments.length < 1) return;
        if (!props.isNew) {
            const saveData = await getAddAttachmentsSaveData(attachments);
            const result = await AppUtils.callService(() => messageService.messageEditAddAttachments(saveData.obj));
            if (!result) return;
            EditItem.setItemIdsFromIds(attachments, result.ids);
        }
        props.onChangeAttachments(props.attachments.concat(attachments));
    };
    // #endregion Attachments

    return (
        <AttachmentList
            direction="row"
            hideThumbnails={false}
            isReadOnly={props.isLockedToOtherUser}
            title={null}
            attachments={props.attachments}
            margin={"0.5rem 0 0 0"}
            onRemoveAttachment={handleAttachmentRemove}
            onDownloadAttachment={handleAttachmentDownload}
            onAddAttachments={handleAttachmentsAdd}
        />
    );
};
